import type { FC, ReactNode } from "react";
import { Navigate, useParams } from "@tanstack/react-router";
import { useAuth } from "@/pages/auth/auth";
import { UnauthorizedModuleFallback } from "@/components/UnauthorizedModuleFallback";
import PageLoader from "@/pages/layout/loaders/PageLoader";
import { useAnalyticsDashboardAccess } from "../hooks/useAnalyticsDashboardAccess";

type TAnalyticsDashboardAccessGuardProps = {
  children: ReactNode;
};

/**
 * Allows the dashboards list when the user has Analytics list RBAC
 * or has at least one dashboard explicitly shared with them.
 */
export const AnalyticsDashboardsListAccessGuard: FC<
  TAnalyticsDashboardAccessGuardProps
> = ({ children }) => {
  const auth = useAuth();
  const { canAccessDashboardList, isLoading } = useAnalyticsDashboardAccess();

  if (!auth.access_token) {
    return <Navigate to="/auth/login" replace />;
  }

  if (isLoading) {
    return <PageLoader />;
  }

  if (!canAccessDashboardList) {
    return <UnauthorizedModuleFallback />;
  }

  return children;
};

/**
 * Allows a dashboard detail page when the user has Analytics detail/list RBAC
 * or the specific dashboard is explicitly shared with them.
 */
export const AnalyticsDashboardDetailAccessGuard: FC<
  TAnalyticsDashboardAccessGuardProps
> = ({ children }) => {
  const auth = useAuth();
  const params = useParams({ strict: false });
  const dashboardId =
    typeof params.dashboardId === "string" ? params.dashboardId : undefined;
  const { canAccessThisDashboard, isLoading } = useAnalyticsDashboardAccess({
    dashboardId,
  });

  if (!auth.access_token) {
    return <Navigate to="/auth/login" replace />;
  }

  if (isLoading) {
    return <PageLoader />;
  }

  if (!canAccessThisDashboard) {
    return <UnauthorizedModuleFallback />;
  }

  return children;
};
